import { useForm } from "@mittwald/flow-components/dist/components/Form";
import LoadingView from "@mittwald/flow-components/dist/components/LoadingView";
import { Section } from "@mittwald/flow-components/dist/components/Section";
import { Text } from "@mittwald/flow-components/dist/components/Text";
import { TextLink } from "@mittwald/flow-components/dist/components/TextLink";
import { TranslationProvider } from "@mittwald/flow-components/dist/components/TranslationProvider";
import { WizardStep } from "@mittwald/flow-components/dist/components/Wizard/components/WizardStep";
import type { WizardState } from "@mittwald/flow-components/dist/components/Wizard/state/model";
import type { AnimationController } from "@mittwald/flow-components/dist/hooks/useAnimationController";
import Suspense from "@mittwald/flow-lib/dist/resources/Suspense";
import type { FC } from "react";
import React from "react";
import { mittwaldApi } from "../../../../api/Mittwald";
import { CodeInput } from "../../../../components/CodeInput";
import { useCodeInputAutoSubmit } from "../../../../hooks/useCodeInputAutoSubmit";
import type { VerifyRegistrationInputs } from "../../../../model/signup/Signup";
import { Signup } from "../../../../model/signup/Signup";
import registerStore from "../../../../store/register";
import VirtualPageViewTracking from "../../../components/tracking/components/VirtualPageViewTracking";
import assertStatus from "@mittwald/api-client/dist/types/assertStatus";

export interface VerificationStepProps {
  email: string;
  rejectionAnimation: AnimationController;
  userId: string;
  trackingId?: string;
  password: string;
  wizardState: WizardState;
}

export const VerificationStep: FC<VerificationStepProps> = (props) => {
  const {
    email,
    rejectionAnimation,
    userId,
    trackingId,
    password,
    wizardState,
  } = props;

  const verificationForm = useForm<VerifyRegistrationInputs>({
    onSubmit: async (values) => {
      const verification = await Signup.verifyRegistration(
        values,
        email,
        password,
        rejectionAnimation,
        userId,
      );

      if (verification === 429) {
        verificationForm.setError("token", { message: "toManyRequests" });
        return false;
      }

      wizardState.gotoNextStep();
    },
  });

  useCodeInputAutoSubmit(verificationForm, "token");

  const resendMail = async (): Promise<void> => {
    const result = await mittwaldApi.userResendVerificationEmail.request({
      requestBody: {
        email,
        userId,
      },
    });

    assertStatus(result, 204);

    registerStore.setEmail(email);
  };

  return (
    <TranslationProvider name="verificationStepContent" type="component">
      <WizardStep
        form={verificationForm}
        id="verification"
        indicatorText="verification"
        noReturn
      >
        <Suspense loadingView={<LoadingView _height={250} />}>
          {trackingId && (
            <VirtualPageViewTracking
              identifier={trackingId}
              name="checkout/register/verification"
            />
          )}
          <Section.Layout>
            <Section.Item headline="verification">
              <Text i18n={{ id: "verificationText", values: { email } }} />
              <TextLink action={resendMail} i18n="resendMailLink" />
            </Section.Item>
            <Section.Item>
              <CodeInput
                label="verificationCode"
                name="token"
                rejectionAnimation={rejectionAnimation}
              />
            </Section.Item>
          </Section.Layout>
        </Suspense>
      </WizardStep>
    </TranslationProvider>
  );
};
